import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Lightbulb } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useMentorTopics } from "@/hooks/useMentorTopics";
import { useCreateMentorshipTopicRequest } from "@/hooks/useMentorshipTopicRequests";
import { useFoundationsEligibility } from "@/hooks/useFoundationsEligibility";
import { FoundationsRequiredBanner } from "@/components/mentorship/FoundationsRequiredBanner";
import { toast } from "sonner";

interface RequestMentorshipByTopicDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type TaxonomyType = "skill" | "industry" | "topic";

export function RequestMentorshipByTopicDialog({ open, onOpenChange }: RequestMentorshipByTopicDialogProps) {
  const [taxonomyType, setTaxonomyType] = useState<TaxonomyType>("topic");
  const [selectedId, setSelectedId] = useState<string>("");
  const [message, setMessage] = useState("");
  const [preferredOutcome, setPreferredOutcome] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const createRequest = useCreateMentorshipTopicRequest();
  
  // Check Foundations course eligibility
  const {
    canRequestMentorship,
    isEnrolled,
    progress,
    courseLink,
    isLoading: eligibilityLoading,
    isMentor,
  } = useFoundationsEligibility();

  // Fetch skills
  const { data: skills, isLoading: skillsLoading } = useQuery({
    queryKey: ["skills"],
    queryFn: async () => {
      const { data, error } = await supabase.from("skills").select("id, name").order("name");
      if (error) throw error;
      return data;
    },
    enabled: open && taxonomyType === "skill",
  });

  // Fetch industries
  const { data: industries, isLoading: industriesLoading } = useQuery({
    queryKey: ["industries"],
    queryFn: async () => {
      const { data, error } = await supabase.from("industries").select("id, name").order("name");
      if (error) throw error;
      return data;
    },
    enabled: open && taxonomyType === "industry",
  });

  // Fetch mentor topics
  const { data: mentorTopics, isLoading: topicsLoading } = useMentorTopics();

  const isLoading = skillsLoading || industriesLoading || topicsLoading;

  const getCurrentOptions = () => {
    switch (taxonomyType) {
      case "skill":
        return skills || [];
      case "industry":
        return industries || [];
      case "topic":
        return mentorTopics || [];
      default:
        return [];
    }
  };

  const handleSubmit = async () => {
    if (!selectedId || !message.trim()) return;

    // Double-check eligibility before submitting
    if (!canRequestMentorship && !isMentor) {
      toast.error("Please complete the Foundations course before requesting mentorship.");
      return;
    }

    const requestData: {
      requested_skill_id?: string;
      requested_industry_id?: string;
      requested_mentor_topic_id?: string;
      request_message: string;
      preferred_outcome?: string;
    } = {
      request_message: message.trim(),
    };

    if (preferredOutcome.trim()) {
      requestData.preferred_outcome = preferredOutcome.trim();
    }

    switch (taxonomyType) {
      case "skill":
        requestData.requested_skill_id = selectedId;
        break;
      case "industry":
        requestData.requested_industry_id = selectedId;
        break;
      case "topic":
        requestData.requested_mentor_topic_id = selectedId;
        break;
    }

    try {
      await createRequest.mutateAsync(requestData);
      setSubmitted(true);
    } catch (error: any) {
      // Check if the error is due to RLS policy (course not completed)
      if (error?.message?.includes("row-level security") || error?.code === "42501") {
        toast.error("Please complete the Foundations course before requesting mentorship.");
      }
      // Other errors handled by mutation
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset form after animation
    setTimeout(() => {
      setTaxonomyType("topic");
      setSelectedId("");
      setMessage("");
      setPreferredOutcome("");
      setSubmitted(false);
    }, 200);
  };

  const canSubmit = selectedId && message.trim().length > 0;

  // Show loading state while checking eligibility
  if (eligibilityLoading) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Show Foundations requirement if not eligible (entrepreneurs only)
  if (!canRequestMentorship && !isMentor) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-primary" />
              Request Mentorship by Topic
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <FoundationsRequiredBanner
              isEnrolled={isEnrolled}
              progress={progress}
              courseLink={courseLink}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (submitted) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-primary" />
              Request Submitted
            </DialogTitle>
          </DialogHeader>
          <div className="py-6 text-center space-y-4">
            <div className="h-16 w-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
              <Lightbulb className="h-8 w-8 text-primary" />
            </div>
            <div className="space-y-2">
              <p className="text-lg font-medium">Thank you!</p>
              <p className="text-sm text-muted-foreground">
                Your request has been sent to the EAB Connect team for review. 
                We'll help connect you with a suitable mentor.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleClose} className="w-full">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-primary" />
            Request Mentorship by Topic
          </DialogTitle>
          <DialogDescription>
            Tell us what kind of mentorship you need and our team will help connect you with a suitable mentor.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Taxonomy Type Selection */}
          <div className="space-y-2">
            <Label>What area do you need help with?</Label>
            <Tabs value={taxonomyType} onValueChange={(v) => { setTaxonomyType(v as TaxonomyType); setSelectedId(""); }}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="topic">Topic</TabsTrigger>
                <TabsTrigger value="skill">Skill</TabsTrigger>
                <TabsTrigger value="industry">Industry</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Selection Dropdown */}
          <div className="space-y-2">
            <Label htmlFor="taxonomy-select">
              Select {taxonomyType === "topic" ? "a topic" : taxonomyType === "skill" ? "a skill" : "an industry"}
            </Label>
            <Select value={selectedId} onValueChange={setSelectedId} disabled={isLoading}>
              <SelectTrigger id="taxonomy-select">
                <SelectValue placeholder={isLoading ? "Loading..." : `Choose ${taxonomyType}...`} />
              </SelectTrigger>
              <SelectContent>
                {getCurrentOptions().map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="request-message">What do you need help with? *</Label>
            <Textarea
              id="request-message"
              placeholder="Describe your specific challenges, questions, or goals..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
            />
          </div>

          {/* Preferred Outcome */}
          <div className="space-y-2">
            <Label htmlFor="preferred-outcome">Preferred outcome (optional)</Label>
            <Input
              id="preferred-outcome"
              placeholder="e.g., Regular meetings, one-time advice, etc."
              value={preferredOutcome}
              onChange={(e) => setPreferredOutcome(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || createRequest.isPending}>
            {createRequest.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Submit Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
